import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Typography,
  Badge,
} from "@altimateai/lego";
import type { UserGroupRulesStats } from "../../services/rbac";

interface UserGroupRulesSectionProps {
  database: string;
  rulesData: UserGroupRulesStats;
}

export const UserGroupRulesSection = ({ database, rulesData }: UserGroupRulesSectionProps) => {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>User Group Rules - {database.toUpperCase()}</CardTitle>
          <CardDescription>Summary of user group access rules for this tenant</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="al-grid al-grid-cols-4 al-gap-4">
            <div className="al-p-4 al-bg-blue-50 al-border al-border-blue-200 al-rounded-lg">
              <Typography className="al-text-blue-700 al-uppercase al-mb-1">Total Rules</Typography>
              <Typography variant="h3" weight="semibold">
                {rulesData.totals.total_rules}
              </Typography>
            </div>
            <div className="al-p-4 al-bg-green-50 al-border al-border-green-200 al-rounded-lg">
              <Typography className="al-text-green-700 al-uppercase al-mb-1">
                User Groups
              </Typography>
              <Typography variant="h3" weight="semibold">
                {rulesData.totals.total_user_groups}
              </Typography>
            </div>
            <div className="al-p-4 al-bg-purple-50 al-border al-border-purple-200 al-rounded-lg">
              <Typography className="al-text-purple-700 al-uppercase al-mb-1">
                Entity Types
              </Typography>
              <Typography variant="h3" weight="semibold">
                {rulesData.totals.total_entity_types}
              </Typography>
            </div>
            <div className="al-p-4 al-bg-orange-50 al-border al-border-orange-200 al-rounded-lg">
              <Typography className="al-text-orange-700 al-uppercase al-mb-1">
                Match Types
              </Typography>
              <Typography variant="h3" weight="semibold">
                {rulesData.totals.total_match_types}
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Rules by Entity Type</CardTitle>
          <CardDescription>What types of Snowflake entities are being filtered</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="al-border al-border-gray-200 al-rounded-lg al-overflow-hidden">
            <table className="al-w-full al-border-collapse">
              <thead className="al-bg-gray-50">
                <tr>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                      Entity Type
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                      Total Rules
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                      User Groups
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody className="al-divide-y al-divide-gray-200">
                {rulesData.by_entity.map((stat, index) => (
                  <tr key={index} className="al-hover:bg-gray-50">
                    <td className="al-px-4 al-py-3">
                      <Badge className="!al-bg-blue-100 !al-text-blue-700 al-border al-border-blue-200">
                        {stat.entity_type}
                      </Badge>
                    </td>
                    <td className="al-px-4 al-py-3">
                      <Typography className="al-font-medium">{stat.total_rules}</Typography>
                    </td>
                    <td className="al-px-4 al-py-3">
                      <Typography className="al-text-gray-600">{stat.user_groups}</Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Rules by Match Type</CardTitle>
          <CardDescription>How filtering patterns are applied</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="al-grid al-grid-cols-3 al-gap-4">
            {rulesData.by_match_type.map((stat, index) => (
              <div
                key={index}
                className="al-p-4 al-bg-gray-50 al-border al-border-gray-200 al-rounded-lg"
              >
                <Badge className="!al-bg-teal-100 !al-text-teal-700 al-border al-border-teal-200 al-mb-2">
                  {stat.match_type}
                </Badge>
                <Typography variant="h3" weight="semibold" className="al-text-gray-900">
                  {stat.rule_count}
                </Typography>
                <Typography className="al-text-gray-500">
                  {((stat.rule_count / rulesData.totals.total_rules) * 100).toFixed(1)}% of total
                </Typography>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Detailed Breakdown</CardTitle>
          <CardDescription>Entity types and match types combination</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="al-border al-border-gray-200 al-rounded-lg al-overflow-hidden">
            <table className="al-w-full al-border-collapse">
              <thead className="al-bg-gray-50">
                <tr>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                      Entity Type
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                      Match Type
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                      Rules
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody className="al-bg-white">
                {rulesData.by_entity_and_match.map((stat, index) => (
                  <tr key={index} className="al-border-b al-border-gray-200">
                    <td className="al-px-4 al-py-4">
                      <Badge className="!al-bg-indigo-100 !al-text-indigo-700 al-border al-border-indigo-200">
                        {stat.entity_type}
                      </Badge>
                    </td>
                    <td className="al-px-4 al-py-4">
                      <Badge className="!al-bg-teal-100 !al-text-teal-700 al-border al-border-teal-200">
                        {stat.match_type}
                      </Badge>
                    </td>
                    <td className="al-px-4 al-py-4">
                      <Typography variant="section-heading">{stat.rule_count}</Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
